// 마을 주민: 역할 NPC(마을①은 미라·브론·세르, ②~④는 그 마을 사람 — shared/townStory, idle)은 머리 위 이름표와 가까울 때 발밑 고리를 갖는다.
// 장식 배우(게시판 옆 의뢰인, 분수 북쪽 산책객)는 충돌·상호작용이 없다. Mixamo idle·walk 클립을 AnimationMixer로 직접 돌리고 실시간 시계를 쓴다(히트스톱 무관).
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { toonGradient } from '../actors/toon';
import { contactMaterial } from './fx';
import { assetUrl } from './kits';
import { VILLAGE_NPCS, townNpcs } from '../../shared/rpg';
import { TOWN_LOOKS } from '../../shared/villageMaps';
import { labelSprite } from './label';
import { getNearNpc } from './npcFocus';
import { gaitPlayback, measureClipGroundSpeed } from '../actors/gait';
import { TURN_WALK, turnToward } from '../actors/turn';
import { angleDelta } from '../util';

type NpcFile = 'npc_shopkeeper' | 'npc_blacksmith' | 'npc_questgiver';

/**
 * 배치: 위치, 바라보는 방향(rad, 0 = +z), 산책이면 왕복 끝점. role = NPC 인덱스(shared/rpg ALL_NPCS, 역할 NPC만), label = 머리 위 이름표,
 * tint = 옷 색 곱(전용 모델이 없는 보석상·연금술사를 같은 모델의 다른 사람으로 보이게, S7)
 */
interface NpcSpot { file: NpcFile; x: number; z: number; rot: number; walk?: { x1: number; z1: number; speed: number }; role?: number; label?: string; tint?: number }

/** 역할 NPC: 판정·UI가 읽는 shared/rpg 데이터 그대로(시설 문 앞) */
export const ROLE_SPOTS: readonly NpcSpot[] = VILLAGE_NPCS.map((n, i) => ({ file: n.model, x: n.x, z: n.z, rot: n.rot, role: i, label: n.name + ' · ' + n.title }));
/** 장식 배우(상호작용 없음) */
export const DECOR_SPOTS: readonly NpcSpot[] = [
  { file: 'npc_questgiver', x: 11.4, z: 8.4, rot: -0.3 }, // 의뢰 게시판 옆
  { file: 'npc_shopkeeper', x: -3.2, z: -7.5, rot: Math.PI / 2, walk: { x1: 3.2, z1: -7.5, speed: 1.1 } }, // 분수 북쪽 산책
];
export const VILLAGE_SPOTS: readonly NpcSpot[] = [...ROLE_SPOTS, ...DECOR_SPOTS];
/** 보석상(푸른 옷)·연금술사(초록 옷) 색 곱. UI 초상 필터(ui/rpgArt NPC_ART_FILTER)와 같은 결 */
const ROLE_TINT: Readonly<Partial<Record<string, number>>> = { jeweler: 0x9fb8ff, alchemist: 0xa8e090, raid: 0xe08a7a };
/** 마을 번호 → 주민 배치(마을①은 VILLAGE_SPOTS, ②~④는 shared/villageMaps 자리 + 마을 장식 배우) */
export function townSpots(town: number): readonly NpcSpot[] {
  if (town <= 1 || !TOWN_LOOKS[town]) return VILLAGE_SPOTS;
  const roles: NpcSpot[] = townNpcs(town).map((n) => ({ file: n.model, x: n.x, z: n.z, rot: n.rot, role: n.index, label: n.name + ' · ' + n.title, tint: n.tint ?? ROLE_TINT[n.role] }));
  const extras: NpcSpot[] = TOWN_LOOKS[town].extras.map((e) => ({ file: 'npc_questgiver', x: e.x, z: e.z, rot: e.rot }));
  return [...roles, ...extras];
}

interface Live {
  root: THREE.Object3D; mixer: THREE.AnimationMixer; spot: NpcSpot; t: number; dir: number; ring: THREE.Mesh | null;
  /** 산책객: 걷기·대기 동작(대기 클립이 없으면 null), 끝점에서 멈춰 돌아서는 남은 시간(초), 그리는 몸 방향(rad) */
  walkA: THREE.AnimationAction | null; idleA: THREE.AnimationAction | null; pause: number; yaw: number;
}

/** 산책 끝점에서 멈춰 서서 돌아서는 시간(초). 예전에는 한 프레임에 180도 돌고 바로 걸었다 */
const NPC_TURN_PAUSE = 0.9;

/** 이름표 높이(키 1.75m 위)와 너비(m) */
const LABEL_Y = 2.35, LABEL_W = 2.7;
/** 가까운 인물 발밑 고리: 고정 밝기(깜빡임·펄스 없음) */
const RING_COLOR = 0xf2c14e, RING_OPACITY = 0.6;

export class VillageNpcs {
  readonly root = new THREE.Group();
  private readonly live: Live[] = [];
  private last = 0;
  /** 읽지 못한 모델 파일 수(load 뒤). 0이면 모두 읽었다 */
  failedModels = 0;
  private readonly owned: { dispose(): void }[] = [];

  constructor() { this.root.name = 'village-npcs'; }

  async load(spots: readonly NpcSpot[] = VILLAGE_SPOTS): Promise<number> {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    const files = [...new Set(spots.map((s) => s.file))];
    const failed: string[] = [];
    const gltfs = new Map<NpcFile, Awaited<ReturnType<GLTFLoader['loadAsync']>>>();
    await Promise.all(files.map(async (f) => {
      try { gltfs.set(f, await loader.loadAsync(assetUrl('npcs/' + f + '.glb'))); }
      catch (err) { failed.push(f); console.warn('마을 NPC 모델을 읽지 못했다: ' + f, err instanceof Error ? err.message : err); }
    }));
    this.failedModels = failed.length;
    if (failed.length > 0) console.warn('마을 NPC 모델 읽기 실패 ' + failed.length + '/' + files.length + '개: ' + failed.join(', '));
    const mats = new Map<string, THREE.Material>();
    // 발밑 접지 그림자(주민 루트의 자식이라 산책객도 따라간다). 그림자 맵만으로는 해 방향으로 빠져 발이 떠 보였다
    const blobGeo = new THREE.PlaneGeometry(2, 2);
    blobGeo.rotateX(-Math.PI / 2);
    const blobMat = contactMaterial(0.55);
    this.owned.push(blobGeo, blobMat);
    const ringGeo = new THREE.RingGeometry(0.66, 0.86, 48);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicMaterial({ color: RING_COLOR, transparent: true, opacity: RING_OPACITY, depthWrite: false, fog: false, side: THREE.DoubleSide });
    ringMat.polygonOffset = true;
    ringMat.polygonOffsetFactor = -3;
    ringMat.polygonOffsetUnits = -3;
    this.owned.push(ringGeo, ringMat);
    for (const spot of spots) {
      const g = gltfs.get(spot.file);
      if (!g) continue;
      const root = cloneSkinned(g.scene);
      // 키 1.75m로 맞추고 발을 바닥에
      const box = new THREE.Box3().setFromObject(root);
      const s = 1.75 / Math.max(0.01, box.max.y - box.min.y);
      root.scale.setScalar(s);
      root.position.set(spot.x, -box.min.y * s, spot.z);
      root.rotation.y = spot.rot;
      const blob = new THREE.Mesh(blobGeo, blobMat);
      // box는 배율 적용 전(로컬) 값이다: 발바닥 높이 + 3cm
      blob.position.set(0, box.min.y + 0.03 / s, 0);
      blob.scale.setScalar(0.5 / s);
      blob.renderOrder = 2;
      blob.frustumCulled = false;
      root.traverse((o) => {
        const m = o as THREE.SkinnedMesh;
        if (!m.isMesh) return;
        m.castShadow = true;
        m.receiveShadow = true;
        m.frustumCulled = false;
        const mk = spot.file + (spot.tint !== undefined ? ':' + spot.tint : '');
        let mat = mats.get(mk);
        if (!mat) {
          const src = m.material as THREE.MeshStandardMaterial;
          // 몹·보스와 같은 툰 그늘(셀 셰이딩 통일). 색 곱이 있으면 그 사람만의 재질
          mat = new THREE.MeshToonMaterial({ map: src.map ?? null, gradientMap: toonGradient(), color: spot.tint ?? 0xffffff });
          mats.set(mk, mat);
          this.owned.push(mat);
        }
        m.material = mat;
      });
      root.add(blob);
      const mixer = new THREE.AnimationMixer(root);
      const clip = g.animations.find((a) => a.name === (spot.walk ? 'walk' : 'idle')) ?? g.animations[0];
      let walkA: THREE.AnimationAction | null = null, idleA: THREE.AnimationAction | null = null;
      if (clip) {
        const a = mixer.clipAction(clip);
        // 걷기 배속 = 산책 속도 / 클립 지면 속도(예전 1배 고정 → 발 미끄러짐). 측정은 재생 전에(측정이 동작을 멈춘다)
        if (spot.walk) {
          const ground = measureClipGroundSpeed(root, mixer, clip) * root.scale.x;
          a.timeScale = gaitPlayback(spot.walk.speed, ground, 0.5, 1.8);
          walkA = a;
          const idleClip = g.animations.find((c) => c.name === 'idle');
          if (idleClip) { idleA = mixer.clipAction(idleClip); idleA.play(); idleA.setEffectiveWeight(0); }
        }
        a.play();
        a.time = (this.live.length * 0.37) % clip.duration; // 같은 몸짓이 맞물리지 않게
      }
      this.root.add(root);
      let ring: THREE.Mesh | null = null;
      if (spot.role !== undefined) {
        // 역할 NPC: 월드 좌표에 머리 위 이름표(스프라이트, 카메라를 본다)와 발밑 고리(가까울 때만 보인다)
        ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.set(spot.x, 0.05, spot.z);
        ring.renderOrder = 3;
        ring.frustumCulled = false;
        ring.visible = false;
        ring.name = 'npc-ring-' + spot.role;
        this.root.add(ring);
        if (spot.label) {
          const sp = labelSprite(spot.label, '#E9D8B4', LABEL_W, 'npc');
          sp.position.set(spot.x, LABEL_Y, spot.z);
          sp.name = 'npc-label-' + spot.role;
          this.root.add(sp);
          this.owned.push(sp.material, sp.material.map!);
        }
      }
      this.live.push({ root, mixer, spot, t: 0, dir: 1, ring, walkA, idleA, pause: 0, yaw: Number.NaN });
    }
    return this.live.length;
  }

  /** 매 프레임(실시간). 산책객은 두 끝점 사이를 오가며 끝에서 돌아선다 */
  update(nowMs: number): void {
    const dt = this.last ? Math.min(0.1, (nowMs - this.last) / 1000) : 0;
    this.last = nowMs;
    const near = getNearNpc();
    for (const l of this.live) {
      l.mixer.update(dt);
      if (l.ring) { const on = l.spot.role === near; if (l.ring.visible !== on) l.ring.visible = on; }
      const w = l.spot.walk;
      if (!w) continue;
      const len = Math.hypot(w.x1 - l.spot.x, w.z1 - l.spot.z);
      const want = Math.atan2((w.x1 - l.spot.x) * l.dir, (w.z1 - l.spot.z) * l.dir);
      if (Number.isNaN(l.yaw)) l.yaw = want;
      // 끝점: 멈춰 서서(대기 동작으로 섞고) 돌아선 뒤 다시 걷는다. 몸이 거의 돌아섰을 때만 출발한다
      if (l.pause > 0) {
        l.pause -= dt;
        l.yaw = turnToward(l.yaw, want, dt, TURN_WALK);
        if (l.pause <= 0 && Math.abs(angleDelta(l.yaw, want)) > 0.35) l.pause = 0.05;
      } else {
        l.t += (dt * w.speed * l.dir) / len;
        if (l.t >= 1 || l.t <= 0) { l.t = Math.min(1, Math.max(0, l.t)); l.dir = -l.dir; l.pause = NPC_TURN_PAUSE; }
        l.yaw = turnToward(l.yaw, want, dt, TURN_WALK);
      }
      const still = l.pause > 0 ? 1 : 0;
      if (l.idleA && l.walkA) {
        const wi = l.idleA.getEffectiveWeight(), k = Math.min(1, dt * 6);
        const ni = wi + (still - wi) * k;
        l.idleA.setEffectiveWeight(ni); l.walkA.setEffectiveWeight(1 - ni);
      }
      l.root.position.x = l.spot.x + (w.x1 - l.spot.x) * l.t;
      l.root.position.z = l.spot.z + (w.z1 - l.spot.z) * l.t;
      l.root.rotation.y = l.yaw;
    }
  }

  dispose(): void {
    for (const o of this.owned) o.dispose();
    this.root.clear();
  }
}
